import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  Linkedin,
  Facebook,
  Instagram,
} from "lucide-react";
import { XIcon } from "./XIcon";

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      {/* Top Bar */}
      <div className="hidden lg:block bg-gradient-to-r from-brand-grey via-brand-grey to-brand-grey text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/10 to-brand-tertiary/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center py-3 text-sm">
            <div className="flex items-center space-x-8">
              <div className="flex items-center group">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-brand-primary to-brand-primary mr-2 animate-pulse"></div>

                <Phone
                  size={14}
                  className="mr-2 text-brand-primary group-hover:scale-110 transition-transform"
                />
                <span className="group-hover:text-brand-primary transition-colors">
                  +256 772 957 990
                </span>
              </div>
              <div className="flex items-center group">
                <Mail
                  size={14}
                  className="mr-2 text-brand-primary group-hover:scale-110 transition-transform"
                />
                <span className="group-hover:text-brand-primary transition-colors">
                  support@kolaborate.co
                </span>
              </div>
              <div className="flex items-center">
                <MapPin size={14} className="mr-2 text-brand-primary" />
                <span className="text-white">Uganda • Kenya • Rwanda</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-200">Join the Movement:</span>
              <div className="flex space-x-3">
                {[
                  {
                    Icon: Linkedin,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://www.linkedin.com/company/kolaborate-platforms/",
                  },
                  {
                    Icon: XIcon,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://twitter.com/kolaborate_p",
                  },
                  {
                    Icon: Facebook,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://www.facebook.com/kolaborateplatforms",
                  },
                  {
                    Icon: Instagram,
                    gradient: "from-brand-primary to-brand-tertiary",
                    href: "https://www.instagram.com/kolaborateplatforms",
                  },
                ].map(({ Icon, gradient, href }, index) => (
                  <a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 rounded-full bg-gradient-to-r ${gradient} flex items-center justify-center hover:scale-110 transition-transform cursor-pointer`}
                  >
                    <Icon size={14} className="text-white" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 lg:py-6">
          <Link to="/" className="flex items-center group">
            <img
              src="/assets/logo-main.png"
              alt="Kolaborate Logo"
              className="h-12 lg:h-16 w-auto transition-all duration-300 group-hover:scale-105 rounded-lg"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative px-4 py-2 font-medium transition-all duration-300 group ${
                    location.pathname === item.path
                      ? "text-brand-primary"
                      : "text-brand-grey hover:text-brand-primary"
                  }`}
                >
                  <span className="relative z-10">{item.name}</span>
                  <div className="absolute inset-0 bg-brand-secondary/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div
                    className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-brand-primary to-brand-tertiary transition-all duration-300 transform ${
                      location.pathname === item.path
                        ? "w-full"
                        : "w-0 group-hover:w-full"
                    }`}
                  ></div>
                </Link>
              ))}
              <div className="flex items-center space-x-3 ml-6">
                {/* <Link
                  to="/contact"
                  className="relative overflow-hidden px-5 py-2.5 rounded-xl border-2 border-green-400 text-green-400 font-semibold group transition-all duration-300 hover:text-gray-900"
                >
                  <span className="relative z-10">Partner With Us</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-green-600 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                </Link> */}
                <Link
                  to="/pods"
                  className="relative overflow-hidden px-6 py-2.5 rounded-xl bg-gradient-to-r from-brand-primary to-brand-tertiary text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <span className="relative z-10">Our Pods</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-tertiary to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-brand-grey hover:text-brand-primary p-2 transition-colors"
            >
              {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg">
          <div className="px-4 pt-6 pb-8 space-y-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`block px-4 py-3 text-lg font-medium border-l-4 transition-all duration-300 rounded-r-lg ${
                  location.pathname === item.path
                    ? "border-brand-primary bg-brand-secondary/50 text-brand-primary"
                    : "border-transparent text-brand-grey hover:border-brand-primary hover:bg-brand-secondary/50 hover:text-brand-primary"
                }`}
              >
                {item.name}
              </Link>
            ))}

            <div className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Phone size={16} className="mr-3 text-brand-primary" />
                  <span>+256 772 957 990</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Mail size={16} className="mr-3 text-brand-primary" />
                  <span>support@kolaborate.co</span>
                </div>
              </div>

              <div className="space-y-3">
                <Link
                  to="/contact"
                  className="block w-full border-2 border-brand-primary text-brand-primary px-6 py-3 rounded-xl text-base font-semibold hover:bg-brand-secondary/50 transition-colors text-center"
                >
                  Contact Sales
                </Link>
                <Link
                  to="/pods"
                  className="block w-full bg-gradient-to-r from-brand-primary to-brand-tertiary text-white px-6 py-3 rounded-xl text-base font-semibold shadow-lg text-center"
                >
                  Our Pods
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
